import { Alert, Button, Input, Select, Spin, Table } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  assignRevenueCategoryVenue,
  createRevenueCategory,
  deactivateRevenueCategory,
  listRevenueCategories,
  reactivateRevenueCategory,
  unassignRevenueCategoryVenue,
  updateRevenueCategory,
} from 'src/utils/revenueCategoryApi'
import { describeRevenueCategoryMultiplier } from 'src/utils/revenueLinePreview'

const { Option } = Select

const VAT_LABELS = {
  standard: 'Standard',
  zeroRated: 'Zero-rated',
  noVat: 'No VAT',
}

const errorMessage = (err, fallback) => {
  const apiErrors = err?.response?.data?.errors
  if (Array.isArray(apiErrors) && apiErrors[0]) return apiErrors[0]
  return err?.response?.data?.message || fallback
}

const VatSelect = ({ value, onChange, label }) => (
  <Select
    aria-label={label}
    allowClear
    placeholder='VAT'
    value={value || undefined}
    onChange={next => onChange(next || '')}
    style={{ width: 140 }}
  >
    <Option value='standard'>Standard</Option>
    <Option value='zeroRated'>Zero-rated</Option>
    <Option value='noVat'>No VAT</Option>
  </Select>
)

const RevenueCategoryList = ({ venueId, readOnly = false }) => {
  const accountVenues = useSelector(state => state.auth?.account?.venues) || []
  const venueAccount = useSelector(state => state.venue?.venue?.account)
  const accountId = venueAccount?._id || venueAccount
  const assignableVenues = accountVenues.filter(
    venue => accountId && String(venue?.account) === String(accountId),
  )
  const [categories, setCategories] = useState(null)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [multiplier, setMultiplier] = useState('1')
  const [vatTreatment, setVatTreatment] = useState('')
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(null)
  const [editName, setEditName] = useState('')
  const [editMultiplier, setEditMultiplier] = useState('1')
  const [editVat, setEditVat] = useState('')

  const load = useCallback(() => {
    if (!venueId) return undefined
    let cancelled = false
    setError('')
    listRevenueCategories(venueId)
      .then(data => {
        if (!cancelled) setCategories(Array.isArray(data) ? data : [])
        return null
      })
      .catch(() => {
        if (!cancelled) {
          setCategories([])
          setError('Revenue categories could not be loaded.')
        }
      })
    return () => {
      cancelled = true
    }
  }, [venueId])

  useEffect(() => {
    setCategories(null)
    return load()
  }, [load])

  const addCategory = async () => {
    setSaving(true)
    setError('')
    const parsedMultiplier =
      multiplier.trim() === '' ? undefined : Number(multiplier)
    const payload = {
      name,
      sortOrder: categories?.length || 0,
      ...(parsedMultiplier !== undefined
        ? { multiplier: parsedMultiplier }
        : {}),
      ...(vatTreatment ? { vatTreatment } : {}),
    }
    try {
      await createRevenueCategory(venueId, payload)
      setName('')
      setMultiplier('1')
      setVatTreatment('')
      load()
    } catch (err) {
      setError(errorMessage(err, 'That category could not be saved.'))
    } finally {
      setSaving(false)
    }
  }

  const startEdit = record => {
    setEditing(record)
    setEditName(record.name || '')
    setEditMultiplier(String(record.multiplier ?? 1))
    setEditVat(record.vatTreatment || '')
    setError('')
  }

  const saveEdit = async () => {
    if (!editing) return
    setSaving(true)
    setError('')
    try {
      await updateRevenueCategory(venueId, editing._id, {
        name: editName,
        multiplier: Number(editMultiplier),
        vatTreatment: editVat,
        sortOrder: editing.sortOrder || 0,
      })
      setEditing(null)
      load()
    } catch (err) {
      setError(errorMessage(err, 'That category could not be updated.'))
    } finally {
      setSaving(false)
    }
  }

  const moveCategory = async (index, direction) => {
    const target = index + direction
    if (!categories || target < 0 || target >= categories.length) return
    const next = [...categories]
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item)
    setSaving(true)
    setError('')
    try {
      await Promise.all(
        next.map((category, sortOrder) =>
          updateRevenueCategory(venueId, category._id, {
            name: category.name,
            multiplier: category.multiplier,
            vatTreatment: category.vatTreatment || '',
            sortOrder,
          }),
        ),
      )
      load()
    } catch (err) {
      setError(errorMessage(err, 'That order could not be saved.'))
    } finally {
      setSaving(false)
    }
  }

  const venueTitle = id => {
    const match =
      accountVenues.find(venue => String(venue?._id) === String(id)) ||
      assignableVenues.find(venue => String(venue?._id) === String(id))
    return match?.title || match?.name || 'Another venue'
  }

  const assignVenue = async (record, assignedVenueId) => {
    if (!assignedVenueId) return
    setSaving(true)
    setError('')
    try {
      await assignRevenueCategoryVenue(venueId, record._id, assignedVenueId)
      load()
    } catch (err) {
      setError(errorMessage(err, 'That venue could not be assigned.'))
    } finally {
      setSaving(false)
    }
  }

  const unassignVenue = async (record, assignedVenueId) => {
    setSaving(true)
    setError('')
    try {
      await unassignRevenueCategoryVenue(venueId, record._id, assignedVenueId)
      load()
    } catch (err) {
      setError(errorMessage(err, 'That venue could not be removed.'))
    } finally {
      setSaving(false)
    }
  }

  const setCategoryActive = async (record, active) => {
    setSaving(true)
    setError('')
    try {
      if (active) {
        await reactivateRevenueCategory(venueId, record._id)
      } else {
        await deactivateRevenueCategory(venueId, record._id)
      }
      load()
    } catch (err) {
      setError(errorMessage(err, 'That category could not be updated.'))
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    {
      title: 'Category',
      dataIndex: 'name',
      key: 'name',
      render: (value, record) =>
        editing?._id === record._id ? (
          <Input
            aria-label='Edit category name'
            value={editName}
            onChange={event => setEditName(event.target.value)}
          />
        ) : (
          value
        ),
    },
    {
      title: 'Entry',
      key: 'mode',
      render: (_, record) => {
        const multiplier =
          editing?._id === record._id
            ? Number(editMultiplier)
            : Number(record.multiplier)
        const label = describeRevenueCategoryMultiplier(multiplier)
        const phrase = label === 'Amount' ? 'Amount' : `Quantity ${label}`
        if (editing?._id !== record._id) return phrase
        return (
          <div>
            <Input
              aria-label='Edit multiplier'
              value={editMultiplier}
              onChange={event => setEditMultiplier(event.target.value)}
            />
            <div>{phrase}</div>
          </div>
        )
      },
    },
    {
      title: 'VAT',
      dataIndex: 'vatTreatment',
      key: 'vatTreatment',
      render: (value, record) =>
        editing?._id === record._id ? (
          <VatSelect
            label='Edit VAT treatment'
            value={editVat}
            onChange={setEditVat}
          />
        ) : (
          VAT_LABELS[value] || '—'
        ),
    },
    {
      title: 'Venues',
      dataIndex: 'venueIds',
      key: 'venues',
      render: (venueIds, record) => {
        const assigned = Array.isArray(venueIds) ? venueIds : []
        const available = assignableVenues.filter(
          venue => !assigned.some(id => String(id) === String(venue?._id)),
        )
        return (
          <div>
            {assigned.map(id => (
              <div key={String(id)} style={{ marginBottom: 4 }}>
                {venueTitle(id)}
                {!readOnly && String(id) !== String(venueId) ? (
                  <>
                    {' '}
                    <Button
                      size='small'
                      aria-label={`Remove ${record.name} from ${venueTitle(
                        id,
                      )}`}
                      disabled={saving}
                      onClick={() => unassignVenue(record, id)}
                    >
                      Remove
                    </Button>
                  </>
                ) : null}
              </div>
            ))}
            {!readOnly && available.length > 0 ? (
              <Select
                aria-label={`Assign ${record.name} to a venue`}
                placeholder='Add venue'
                value={undefined}
                disabled={saving}
                style={{ width: 180 }}
                onChange={assignedVenueId =>
                  assignVenue(record, assignedVenueId)
                }
              >
                {available.map(venue => (
                  <Option key={venue._id} value={venue._id}>
                    {venue.title || venue.name}
                  </Option>
                ))}
              </Select>
            ) : null}
          </div>
        )
      },
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: value => (value === false ? 'No' : 'Yes'),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record, index) =>
        editing?._id === record._id ? (
          <span>
            <Button type='primary' loading={saving} onClick={saveEdit}>
              Save
            </Button>{' '}
            <Button onClick={() => setEditing(null)}>Cancel</Button>
          </span>
        ) : (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
            <Button
              size='small'
              aria-label={`Move ${record.name} up`}
              disabled={index === 0 || saving}
              onClick={() => moveCategory(index, -1)}
            >
              Up
            </Button>
            <Button
              size='small'
              aria-label={`Move ${record.name} down`}
              disabled={
                !categories || index === categories.length - 1 || saving
              }
              onClick={() => moveCategory(index, 1)}
            >
              Down
            </Button>
            <Button size='small' onClick={() => startEdit(record)}>
              Edit
            </Button>
            {record.active === false ? (
              <Button
                size='small'
                onClick={() => setCategoryActive(record, true)}
              >
                Reactivate
              </Button>
            ) : (
              <Button
                size='small'
                onClick={() => setCategoryActive(record, false)}
              >
                Deactivate
              </Button>
            )}
          </span>
        ),
    },
  ]

  return (
    <section
      style={{
        background: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: 12,
        padding: 24,
        margin: '0 0 24px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
      aria-label='Revenue categories'
    >
      <h2 style={{ fontSize: 18, marginBottom: categoriesOpen ? 8 : 0 }}>
        <button
          type='button'
          aria-expanded={categoriesOpen}
          onClick={() => setCategoriesOpen(current => !current)}
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          {categoriesOpen ? '▾' : '▸'} Revenue categories
        </button>
      </h2>
      {categoriesOpen ? (
        <>
          {readOnly ? null : (
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 12,
                flexWrap: 'wrap',
              }}
            >
              <Input
                aria-label='Category name'
                placeholder='Category name'
                value={name}
                onChange={event => setName(event.target.value)}
                style={{ width: 220 }}
              />
              <Input
                aria-label='Multiplier'
                placeholder='Multiplier'
                value={multiplier}
                onChange={event => setMultiplier(event.target.value)}
                style={{ width: 120 }}
              />
              <VatSelect
                label='VAT treatment'
                value={vatTreatment}
                onChange={setVatTreatment}
              />
              <Button type='primary' loading={saving} onClick={addCategory}>
                Add category
              </Button>
            </div>
          )}
          {error ? <Alert type='error' message={error} showIcon /> : null}
          {categories == null ? (
            <Spin />
          ) : (
            <Table
              rowKey={record => record._id || record.name}
              columns={
                readOnly
                  ? columns.filter(column => column.key !== 'actions')
                  : columns
              }
              dataSource={categories}
              pagination={false}
              locale={{ emptyText: 'No revenue categories yet.' }}
            />
          )}
        </>
      ) : null}
    </section>
  )
}

export default RevenueCategoryList
